// Raw WebSocket implementation

import type {
  NotificationData,
  WebSocketEventHandlers,
  WebSocketMessage,
  AlphaDiscoveryData,
  FollowingUpdateData,
  PostDiscoveryData,
  SystemStatusData,
  WebSocketError
} from '@/types/websocket';

class WebSocketService {
  private socket: WebSocket | null = null;
  private token: string | null = null;
  private isAuthenticated = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private handlers: WebSocketEventHandlers = {};
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;

  constructor() {
    // Don't auto-connect in constructor - let components control connection
    // This prevents multiple connections during development
  }

  /**
   * Connect to WebSocket server
   * @param token - JWT authentication token
   */
  connect(token: string): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      return;
    }
    
    if (this.socket?.readyState === WebSocket.CONNECTING) {
      return;
    }

    this.token = token;
    
    // Get user data from localStorage to extract user_id
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      const userId = user.id || user._id;
      if (!userId) {
        return;
      }
      
      const wsBaseUrl = import.meta.env.VITE_WS_URL || 'wss://ws-tt.vitrioll.com';
      const wsUrl = `${wsBaseUrl}/ws/${userId}?token=${token}`;

      this.socket = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      this.handleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.isAuthenticated = false;
      this.reconnectAttempts = 0;
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.isAuthenticated = true;
      this.handlers.onConnect?.();
      
      // Start ping timer to keep connection alive
      this.startPing();
    };

    this.socket.onclose = (event) => {
      this.isAuthenticated = false;
      this.handlers.onDisconnect?.(event.reason || 'Connection closed');
      
      // Clear ping timer
      if (this.pingTimer) {
        clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
      
      // Auto-reconnect unless it was a normal close
      if (event.code !== 1000) {
        this.handleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      this.handlers.onError?.(error);
    };

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
      }
    };
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    // Only log non-pong messages to reduce clutter
    
    if (!message.type) {
      return;
    }

    // Only log non-pong message types

    switch (message.type) {
      case 'alpha_discovery':
        this.handlers.onAlphaDiscovery?.(message.data);
        this.showDesktopNotification({
          type: 'alpha_discovery',
          data: message.data,
          message: `New alpha discovered: ${message.data?.name} (@${message.data?.profile})`
        });
        break;

      case 'following_update':
        this.handlers.onFollowingUpdate?.(message.data);
        this.showDesktopNotification({
          type: 'following_update',
          data: message.data,
          message: `${message.data?.follower_username} followed ${message.data?.followed_username}`
        });
        break;
        
      case 'new_follow':
        this.handlers.onFollowingUpdate?.(message.data);
        const followerUsername = message.username || message.data?.tracked_username;
        const followCount = message.data?.new_follow_count || 0;
        const newFollows = message.data?.new_follows || [];
        this.showDesktopNotification({
          type: 'following_update',
          data: message.data,
          message: `${followerUsername} started following ${followCount} new account${followCount > 1 ? 's' : ''}: ${newFollows.slice(0, 3).join(', ')}${followCount > 3 ? '...' : ''}`
        });
        break;

      case 'post_discovery':
        this.handlers.onPostDiscovery?.(message.data);
        this.showDesktopNotification({
          type: 'post_discovery',
          data: message.data,
          message: `New post from ${message.data?.author_username}: ${message.data?.text?.substring(0, 50)}...`
        });
        break;
        
      case 'new_post':
        this.handlers.onPostDiscovery?.(message.data);
        const username = message.username || message.data?.tracked_username;
        const postText = message.data?.latest_posts?.[0]?.text || '';
        this.showDesktopNotification({
          type: 'post_discovery',
          data: message.data,
          message: `New post from ${username}: ${postText.substring(0, 50)}...`
        });
        break;

      case 'system_status':
        this.handlers.onSystemStatus?.(message.data);
        break;

      case 'error':
        this.handlers.onError?.(message.data);
        break;

      case 'pong':
        // Handle ping/pong for connection health
        break;

      default:
        // Unknown message type
    }
  }

  /**
   * Handle reconnection logic
   */
  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

    this.reconnectTimer = window.setTimeout(() => {
      if (this.token) {
        this.connect(this.token);
      }
    }, delay);
  }

  /**
   * Start ping timer to keep connection alive
   */
  private startPing(): void {
    this.pingTimer = window.setInterval(() => {
      this.ping();
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Set event handlers
   * @param handlers - Event handler functions
   */
  setHandlers(handlers: WebSocketEventHandlers): void {
    // If empty object passed, clear all handlers
    if (Object.keys(handlers).length === 0) {
      this.handlers = {};
    } else {
      this.handlers = { ...this.handlers, ...handlers };
    }
  }

  /**
   * Send ping to check connection health
   */
  ping(): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: 'ping' }));
    }
  }

  /**
   * Show desktop notification for various events
   * @param notification - Notification data
   */
  private showDesktopNotification(notification: NotificationData): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      let title = '';
      let body = '';
      let icon = '/vite.svg';
      
      if (notification.type === 'alpha_discovery') {
        title = 'Alpha Discovery';
        body = notification.message || `New alpha discovered: ${notification.data?.name}`;
        icon = notification.data?.avatar || '/vite.svg';
      } else if (notification.type === 'following_update') {
        title = 'Following Update';
        body = notification.message || 'Someone started following a new account';
        icon = '/vite.svg';
      } else if (notification.type === 'post_discovery') {
        title = 'New Post';
        body = notification.message || 'New post discovered';
        icon = `/twitter-avatar/${notification.data?.author_username}` || '/vite.svg';
      }
      
      new Notification(title, {
        body,
        icon,
        tag: 'twitter-tracker'
      });
    }
  }

  /**
   * Request notification permission
   */
  requestNotificationPermission(): void {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
      });
    }
  }

  /**
   * Get connection status
   */
  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /**
   * Get authentication status
   */
  get authenticated(): boolean {
    return this.isAuthenticated;
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;